import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Field, Form, Input, Link, TextArea, Typography } from '@/components';
import { SaleOption } from '@/features/opportunities/types';
import { nullableStringSetter } from '@/helpers';
import { useCountries, useSaleOptions } from '@/hooks';
import { AdvisoryServiceForm, AdvisoryServiceFormFields } from '@/features/services';
import { useFieldArray, UseFormRegister } from 'react-hook-form';
import { Route } from '@/constants';
import { ProjectFields } from './project-fields';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type ProjectTechnologyField = {
  enabled: boolean;
};

export type ProjectForm = {
  country: string;
  city: string;
  solar: ProjectTechnologyField;
  wind: ProjectTechnologyField;
  bess: ProjectTechnologyField;
  hydrogen: ProjectTechnologyField;
  biogas: ProjectTechnologyField;
  hidropower: ProjectTechnologyField;
  datacenter: ProjectTechnologyField;
  topco: ProjectTechnologyField;
};

export type CreateOpportunityFormFields = {
  // Opportunity Form Fields
  name: string;
  minimum_percentage: number;
  maximum_percentage: number;
  options: SaleOption[];
  comments: string | null;
  projects: ProjectForm[];
  about_you: string | null;
  status: string;

  // Advisory service
  advisory_service_information: {
    legal_advisors: string;
    financial_advisors: string;
    technical_advisors: string;
    epc_service: string;
    //om_service: string;
    //other: string;
  };
};

export type CreateOpportunityFormProps = {
  readonly id: string;
  readonly onSubmit: (data: CreateOpportunityFormFields) => void;
};

export function CreateOpportunityForm({ id, onSubmit }: CreateOpportunityFormProps) {
  const { t } = useTranslation(['opportunities']);
  const { options: countriesOptions } = useCountries();
  const { options: mapSaleOption } = useSaleOptions();

  const options = {
    defaultValues: {
      name: '',
      minimum_percentage: 0,
      maximum_percentage: 100,
      options: [],
      comments: null,
      projects: [
        {
          country: 'ES',
          city: '',
          solar: { enabled: false },
          wind: { enabled: false },
          bess: { enabled: false },
          hydrogen: { enabled: false },
          biogas: { enabled: false },
          topco: { enabled: false },
        },
      ],
      about_you: null,

      advisory_service_information: {
        legal_advisors: '',
        financial_advisors: '',
        technical_advisors: '',
        epc_service: '',
        //om_service: '',
        //other: '',
      },
    },
  };

  return (
    <Form<CreateOpportunityFormFields> id={id} onSubmit={onSubmit} options={options}>
      {({ formState, register, watch, control }) => {
        const { fields, append, remove } = useFieldArray({
          control,
          name: 'projects',
        });
        return (
          <div className="space-y-6">
            <Typography as="p" color="primary" size="body-3" weight="normal" className="mt-1">
              {t(
                'Please, fill in the general details of the project and continue completing all required fields. Before your project is published, a member of the nTeaser team will reach out to review the information.'
              )}
            </Typography>

            <Typography as="p" color="primary" size="body-3" weight="normal" className="mt-1">
              {t('If you have any questions, feel free to contact us at ')}
              <Link to="mailto:projects@nteaser.com">projects@nteaser.com</Link>
            </Typography>
            <hr />
            <div>
              <Typography as="h4" color="primary" size="title" weight="semibold">
                {t('Sale process')}
              </Typography>
              <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1">
                {t(
                  'Please provide the key details of the sale process for your desired transaction. Include relevant information to guide potential buyers smoothly through each step of the transaction.'
                )}
              </Typography>
            </div>

            <Field
              id="name"
              label={t('Project title')}
              error={formState.errors['name']}
              delay={0}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                id="name"
                color={formState.errors['name'] ? 'error' : 'primary'}
                autoComplete="off"
                registration={register('name', {
                  required: t('This field is required'),
                  setValueAs: nullableStringSetter,
                })}
              />
            </Field>

            <Field id="minimum_percentage" label={t('Percentage range')} delay={0.3}>
              <div className="flex w-full items-center">
                <div className=" ">
                  <Input
                    id="minimum_percentage"
                    placeholder="0"
                    color={formState.errors['minimum_percentage'] ? 'error' : 'primary'}
                    registration={register('minimum_percentage', {
                      required: t('This field is required'),
                      max: 100,
                      ...NUMERIC,
                    })}
                    type="number"
                    suffix="%"
                  />
                </div>
                <span className="px-3">-</span>
                <div className="">
                  <Input
                    id="maximum_percentage"
                    placeholder="100"
                    color={formState.errors['maximum_percentage'] ? 'error' : 'primary'}
                    registration={register('maximum_percentage', {
                      required: t('This field is required'),
                      max: 100,
                      ...NUMERIC,
                    })}
                    type="number"
                    suffix="%"
                  />
                </div>
              </div>
            </Field>

            <Field label={t('Deal Type')} delay={0.45} classes={{ field: 'space-y-2', input: 'flex flex-wrap gap-3' }}>
              {mapSaleOption.map((option) => (
                <Checkbox color="primary" registration={register('options')} value={option.value}>
                  {option.label}
                </Checkbox>
              ))}
            </Field>

            <Field id="comments" label={t('Process details')} error={formState.errors['comments']} delay={0.6}>
              <TextArea
                id="comments"
                color="primary"
                placeholder="Type here..."
                registration={register('comments', { setValueAs: nullableStringSetter })}
              />
            </Field>

            <hr />

            <div>
              <Typography as="h4" color="primary" size="title" weight="semibold">
                {t('Projects')}
              </Typography>
              <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1">
                {t(
                  'Please, fill in the main information of the project. You can create a portfolio by adding as many projects as you require.'
                )}
              </Typography>

              {fields.map((field, index) => (
                <ProjectFields
                  key={field.id}
                  index={index}
                  register={register}
                  errors={formState.errors}
                  remove={index > 0 ? () => remove(index) : undefined}
                  countriesOptions={countriesOptions}
                  watch={watch}
                />
              ))}
              <Button
                className="mt-16"
                color="neutral"
                onClick={() =>
                  append({
                    country: 'ES',
                    city: '',
                    solar: { enabled: false },
                    wind: { enabled: false },
                    bess: { enabled: false },
                    hydrogen: { enabled: false },
                    biogas: { enabled: false },
                    hidropower: { enabled: false },
                    datacenter: { enabled: false },
                    topco: { enabled: false },
                  })
                }
              >
                Add another project
              </Button>
            </div>
            <hr />

            <div>
              <Typography as="h4" color="primary" size="title" weight="semibold">
                {t('About the seller')}
              </Typography>
              <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1">
                {t(
                  'Please complete this section with an anonymous summary of you relevant experience in the development, operation, and management of similar assets. Provide details on you expertise and track record in the renewable energy sector.'
                )}
              </Typography>
            </div>

            <Field id="about_you" label={t('')} delay={1}>
              <TextArea
                id="about_you"
                color="primary"
                placeholder="Type here..."
                registration={register('about_you', { setValueAs: nullableStringSetter })}
              />
            </Field>

            <hr />
            <div>
              <Typography as="h4" color="primary" size="title" weight="semibold">
                {t('Advisory Services')}
              </Typography>
              <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1">
                {t(
                  'Do you need professional advice for your project? At nTeaser, we have selected the best advisors to guide you. Selecting "Yes" is non-binding; we will only connect you with the right expert! Want to know more? Visit our'
                )}{' '}
                <Link to={Route.Services} target="_blank">
                  {t('Advisors section')}
                </Link>{' '}
                {t('and discover them all!.')}
              </Typography>
            </div>

            <AdvisoryServiceForm
              register={register as UseFormRegister<AdvisoryServiceFormFields>}
              formState={formState}
            />
          </div>
        );
      }}
    </Form>
  );
}
